/**
 * Utility functions for generating time-related options dynamically
 */

export interface TimeOption {
    value: string | number;
    label: string;
}

export interface DurationOption {
    value: string | number;
    label: string;
    hours?: number;
}

export interface MinuteOption {
    value: number;
    label: string;
}

// Time value format types
export type TimeValueFormat = '12h' | '24h' | 'integer' | 'minutes';

// Duration value format types
export type DurationValueFormat = 'description' | 'hours' | 'integer';

/**
 * Convert time to different value formats
 * @param time - Date object
 * @param format - Desired value format
 * @returns Formatted value
 */
const formatTimeValue = (time: Date, format: TimeValueFormat): string | number => {
    switch (format) {
        case '12h':
            return time.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
        case '24h':
            // Built by hand: en-US with hour12:false uses an h24 clock, so
            // midnight came back as "24:00" instead of "00:00".
            return `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(2, '0')}`;
        case 'integer':
            const hour = time.getHours();
            const minute = time.getMinutes();
            return hour * 100 + minute; // 08:00 -> 800, 14:00 -> 1400
        case 'minutes':
            return time.getHours() * 60 + time.getMinutes(); // Total minutes since midnight
        default:
            return time.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
    }
};

/**
 * Generate time options with customizable interval and format
 * @param interval - Minutes between each time option (default: 15)
 * @param startHour - Starting hour in 24-hour format (default: 0)
 * @param endHour - Ending hour in 24-hour format (default: 24)
 * @param displayFormat - Display format: '12h' or '24h' (default: '12h')
 * @param valueFormat - Value format: '12h', '24h', 'integer', 'minutes' (default: '12h')
 * @returns Array of time options
 * @example
 * generateTimeOptions(15, 0, 24, '12h', 'integer')
 * // Returns: [
 * //   { value: 0, label: '12:00 AM' },
 * //   { value: 15, label: '12:15 AM' },
 * //   { value: 30, label: '12:30 AM' },
 * //   { value: 45, label: '12:45 AM' },
 * //   { value: 100, label: '1:00 AM' },
 * //   ... (96 total options)
 * // ]
 */
export const generateTimeOptions = (
    interval = 15,
    startHour = 0,
    endHour = 24,
    displayFormat: '12h' | '24h' = '12h',
    valueFormat: TimeValueFormat = '12h'
): TimeOption[] => {
    const options: TimeOption[] = [];

    for (let hour = startHour; hour < endHour; hour++) {
        for (let minute = 0; minute < 60; minute += interval) {
            const time = new Date();
            time.setHours(hour, minute, 0, 0);

            let label: string;
            if (displayFormat === '12h') {
                label = time.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
            } else {
                label = time.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            }

            options.push({
                value: formatTimeValue(time, valueFormat),
                label: label
            });
        }
    }

    return options;
};

/**
 * Generate standard time options (15-minute intervals, 12-hour format)
 * @param valueFormat - Value format: '12h', '24h', 'integer', 'minutes' (default: '12h')
 * @returns Array of time options
 * @example
 * getStandardTimeOptions('integer')
 * // Returns: [
 * //   { value: 0, label: '12:00 AM' },
 * //   { value: 15, label: '12:15 AM' },
 * //   { value: 30, label: '12:30 AM' },
 * //   ... (96 total options)
 * // ]
 */
export const getStandardTimeOptions = (valueFormat: TimeValueFormat = '12h'): TimeOption[] => {
    return generateTimeOptions(15, 0, 24, '12h', valueFormat);
};

/**
 * Generate business hours time options
 * @param startHour - Business start hour (default: 6)
 * @param endHour - Business end hour (default: 20)
 * @param interval - Minutes between options (default: 15)
 * @param valueFormat - Value format: '12h', '24h', 'integer', 'minutes' (default: '12h')
 * @returns Array of time options for business hours
 * @example
 * getBusinessTimeOptions(8, 18, 30, 'integer')
 * // Returns: [
 * //   { value: 800, label: '8:00 AM' },
 * //   { value: 830, label: '8:30 AM' },
 * //   { value: 900, label: '9:00 AM' },
 * //   ... (20 total options)
 * // ]
 */
export const getBusinessTimeOptions = (
    startHour = 6,
    endHour = 20,
    interval = 15,
    valueFormat: TimeValueFormat = '12h'
): TimeOption[] => {
    return generateTimeOptions(interval, startHour, endHour, '12h', valueFormat);
};

/**
 * Convert duration to different value formats
 * @param hours - Number of hours
 * @param format - Desired value format
 * @returns Formatted value
 */
const formatDurationValue = (hours: number, format: DurationValueFormat): string | number => {
    switch (format) {
        case 'description':
            return `Up to ${hours} hours`;
        case 'hours':
            return `${hours} hours`;
        case 'integer':
            return hours;
        default:
            return `Up to ${hours} hours`;
    }
};

/**
 * Generate duration options with customizable ranges
 * @param maxHours - Maximum hours to include (default: 12)
 * @param interval - Hours between each option (default: 2)
 * @param displayFormat - Display format: 'description' or 'hours' (default: 'description')
 * @param valueFormat - Value format: 'description', 'hours', 'integer' (default: 'description')
 * @returns Array of duration options
 * @example
 * generateDurationOptions(8, 2, 'description', 'integer')
 * // Returns: [
 * //   { value: 2, label: 'Up to 2 hours', hours: 2 },
 * //   { value: 4, label: 'Up to 4 hours', hours: 4 },
 * //   { value: 6, label: 'Up to 6 hours', hours: 6 },
 * //   { value: 8, label: 'Up to 8 hours', hours: 8 }
 * // ]
 */
export const generateDurationOptions = (
    maxHours = 12,
    interval = 2,
    displayFormat: 'description' | 'hours' = 'description',
    valueFormat: DurationValueFormat = 'description'
): DurationOption[] => {
    const options: DurationOption[] = [];

    for (let hours = interval; hours <= maxHours; hours += interval) {
        let label: string;
        if (displayFormat === 'description') {
            label = `Up to ${hours} hours`;
        } else {
            label = `${hours} hours`;
        }

        options.push({
            value: formatDurationValue(hours, valueFormat),
            label: label,
            hours: hours
        });
    }

    return options;
};

/**
 * Generate standard duration options (2-hour intervals up to 12 hours)
 * @param valueFormat - Value format: 'description', 'hours', 'integer' (default: 'description')
 * @returns Array of duration options
 * @example
 * getStandardDurationOptions('integer')
 * // Returns: [
 * //   { value: 2, label: 'Up to 2 hours', hours: 2 },
 * //   { value: 4, label: 'Up to 4 hours', hours: 4 },
 * //   { value: 6, label: 'Up to 6 hours', hours: 6 },
 * //   { value: 8, label: 'Up to 8 hours', hours: 8 },
 * //   { value: 10, label: 'Up to 10 hours', hours: 10 },
 * //   { value: 12, label: 'Up to 12 hours', hours: 12 }
 * // ]
 */
export const getStandardDurationOptions = (valueFormat: DurationValueFormat = 'description'): DurationOption[] => {
    return generateDurationOptions(12, 2, 'description', valueFormat);
};

/**
 * Generate minute-based options with customizable intervals
 * Useful for grace periods, timeouts, intervals, etc.
 * @param maxMinutes - Maximum minutes to include (default: 60)
 * @param intervals - Array of minute intervals (default: [0, 5, 10, 15, 30, 45, 60])
 * @returns Array of minute options
 * @example
 * generateMinuteOptions(30, [0, 5, 10, 15, 30])
 * // Returns: [
 * //   { value: 0, label: '0 min' },
 * //   { value: 5, label: '5 min' },
 * //   { value: 10, label: '10 min' },
 * //   { value: 15, label: '15 min' },
 * //   { value: 30, label: '30 min' }
 * // ]
 */
export const generateMinuteOptions = (
    maxMinutes = 60,
    intervals: number[] = [0, 5, 10, 15, 30, 45, 60]
): MinuteOption[] => {
    return intervals
        .filter(interval => interval <= maxMinutes)
        .map(interval => ({
            value: interval,
            label: interval === 0 ? '0 min' : interval === 60 ? '1 hour' : `${interval} min`
        }));
};

/**
 * Generate standard minute options (common intervals up to 60 minutes)
 * @returns Array of minute options
 * @example
 * getStandardMinuteOptions()
 * // Returns: [
 * //   { value: 0, label: '0 min' },
 * //   { value: 5, label: '5 min' },
 * //   { value: 10, label: '10 min' },
 * //   { value: 15, label: '15 min' },
 * //   { value: 30, label: '30 min' },
 * //   { value: 45, label: '45 min' },
 * //   { value: 60, label: '1 hour' }
 * // ]
 */
export const getStandardMinuteOptions = (): MinuteOption[] => {
    return generateMinuteOptions();
};

/**
 * Generate grace period options (alias for generateMinuteOptions)
 * @param maxMinutes - Maximum minutes to include (default: 60)
 * @param intervals - Array of minute intervals (default: [0, 5, 10, 15, 30, 45, 60])
 * @returns Array of grace period options
 * @example
 * generateGracePeriodOptions(30)
 * // Returns: [
 * //   { value: 0, label: '0 min' },
 * //   { value: 5, label: '5 min' },
 * //   { value: 10, label: '10 min' },
 * //   { value: 15, label: '15 min' },
 * //   { value: 30, label: '30 min' }
 * // ]
 */
export const generateGracePeriodOptions = (
    maxMinutes = 60,
    intervals: number[] = [0, 5, 10, 15, 30, 45, 60]
): MinuteOption[] => {
    return generateMinuteOptions(maxMinutes, intervals);
};

/**
 * Generate standard grace period options (alias for getStandardMinuteOptions)
 * @returns Array of grace period options
 * @example
 * getStandardGracePeriodOptions()
 * // Returns: [
 * //   { value: 0, label: '0 min' },
 * //   { value: 5, label: '5 min' },
 * //   { value: 10, label: '10 min' },
 * //   { value: 15, label: '15 min' },
 * //   { value: 30, label: '30 min' },
 * //   { value: 45, label: '45 min' },
 *   { value: 60, label: '1 hour' }
 * // ]
 */
export const getStandardGracePeriodOptions = (): MinuteOption[] => {
    return getStandardMinuteOptions();
};

/**
 * Generate custom time range options
 * @param startTime - Start time in HH:mm format
 * @param endTime - End time in HH:mm format
 * @param interval - Minutes between options (default: 15)
 * @param valueFormat - Value format: '12h', '24h', 'integer', 'minutes' (default: '12h')
 * @returns Array of time options for the specified range
 * @example
 * getCustomTimeRangeOptions('09:00', '17:00', 60, 'integer')
 * // Returns: [
 * //   { value: 900, label: '9:00 AM' },
 * //   { value: 1000, label: '10:00 AM' },
 * //   { value: 1100, label: '11:00 AM' },
 * //   { value: 1200, label: '12:00 PM' },
 * //   { value: 1300, label: '1:00 PM' },
 * //   { value: 1400, label: '2:00 PM' },
 * //   { value: 1500, label: '3:00 PM' },
 * //   { value: 1600, label: '4:00 PM' },
 * //   { value: 1700, label: '5:00 PM' }
 * // ]
 */
export const getCustomTimeRangeOptions = (
    startTime: string,
    endTime: string,
    interval = 15,
    valueFormat: TimeValueFormat = '12h'
): TimeOption[] => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startTotalMinutes = startHour * 60 + startMinute;
    const endTotalMinutes = endHour * 60 + endMinute;

    const options: TimeOption[] = [];

    for (let minutes = startTotalMinutes; minutes <= endTotalMinutes; minutes += interval) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;

        const time = new Date();
        time.setHours(hour, minute, 0, 0);

        const label = time.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });

        options.push({
            value: formatTimeValue(time, valueFormat),
            label: label
        });
    }

    return options;
};

/**
 * Convert time string to minutes for calculations
 * @param timeString - Time string in format "h:mm a" or "HH:mm"
 * @returns Minutes since midnight
 * @example
 * timeStringToMinutes('2:30 PM')
 * // Returns: 870
 *
 * timeStringToMinutes('14:30')
 * // Returns: 870
 */
export const timeStringToMinutes = (timeString: string): number => {
    const time = new Date(`2000-01-01 ${timeString}`);
    return time.getHours() * 60 + time.getMinutes();
};

/**
 * Convert minutes to time string
 * @param minutes - Minutes since midnight
 * @param format - Output format: '12h' or '24h' (default: '12h')
 * @returns Time string
 * @example
 * minutesToTimeString(870, '12h')
 * // Returns: '2:30 PM'
 *
 * minutesToTimeString(870, '24h')
 * // Returns: '14:30'
 */
export const minutesToTimeString = (minutes: number, format: '12h' | '24h' = '12h'): string => {
    const time = new Date();
    time.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);

    return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: format === '12h'
    });
};

/**
 * Convert integer time format to display string
 * @param timeInteger - Time in integer format (e.g., 800 for 8:00 AM, 1400 for 2:00 PM)
 * @param format - Output format: '12h' or '24h' (default: '12h')
 * @returns Time string
 * @example
 * integerTimeToString(800, '12h')
 * // Returns: '8:00 AM'
 *
 * integerTimeToString(1400, '24h')
 * // Returns: '14:00'
 */
export const integerTimeToString = (timeInteger: number, format: '12h' | '24h' = '12h'): string => {
    const hours = Math.floor(timeInteger / 100);
    const minutes = timeInteger % 100;

    const time = new Date();
    time.setHours(hours, minutes, 0, 0);

    return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: format === '12h'
    });
};

/**
 * Convert time string to integer format
 * @param timeString - Time string in format "h:mm a" or "HH:mm"
 * @returns Integer time format
 * @example
 * timeStringToInteger('8:00 AM')
 * // Returns: 800
 *
 * timeStringToInteger('2:30 PM')
 * // Returns: 1430
 */
export const timeStringToInteger = (timeString: string): number => {
    const time = new Date(`2000-01-01 ${timeString}`);
    return time.getHours() * 100 + time.getMinutes();
};
